import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type HelpSection = {
	title: string;
	body?: ReactNode;
	items?: ReactNode[];
};

export type PageHelp = {
	title: string;
	description: string;
	visual?: ReactNode;
	sections: HelpSection[];
};

type PageHeaderProps = {
	moduleId?: string;
	title: string;
	description?: string;
	help?: PageHelp;
	children?: ReactNode;
	className?: string;
};

export function PageHeader({
	moduleId,
	title,
	description,
	help,
	children,
	className,
}: PageHeaderProps) {
	return (
		<header
			className={cn("flex flex-col gap-3 md:flex-row md:items-start md:justify-between", className)}
		>
			<div className="min-w-0 space-y-2" data-module-id={moduleId}>
				<h2 className="text-xl font-semibold text-text-primary">{title}</h2>
				{description ? (
					<p className="max-w-3xl text-sm leading-6 text-text-secondary">{description}</p>
				) : null}
			</div>
			<div className="flex flex-wrap items-center gap-2">
				{children}
				{help ? <PageHelpDialog help={help} /> : null}
			</div>
		</header>
	);
}

function PageHelpDialog({ help }: { help: PageHelp }) {
	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button type="button" variant="outline" size="sm">
					<HelpCircle className="mr-2 h-4 w-4" aria-hidden="true" />
					页面说明
				</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{help.title}</DialogTitle>
					<DialogDescription>{help.description}</DialogDescription>
				</DialogHeader>
				{help.visual ? <div>{help.visual}</div> : null}
				<div className="space-y-5">
					{help.sections.map((section) => (
						<section key={section.title} className="space-y-2">
							<h3 className="text-sm font-semibold text-text-primary">{section.title}</h3>
							{section.body ? (
								<div className="text-sm leading-6 text-text-secondary">{section.body}</div>
							) : null}
							{section.items ? (
								<ul className="space-y-2 text-sm leading-6 text-text-secondary">
									{section.items.map((item, index) => (
										<li key={`${section.title}-${index}`} className="flex gap-2">
											<span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary" />
											<span>{item}</span>
										</li>
									))}
								</ul>
							) : null}
						</section>
					))}
				</div>
			</DialogContent>
		</Dialog>
	);
}
